import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StickerPrintDialog from "../components/StickerPrintDialog";
import { ensureConnected, findDefaultPrinter, printRawBatch, isQzActive } from "../lib/qz";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, Layers } from "lucide-react";

/**
 * Stock-in / sticker-print landing page for a GRN.
 * Browser print is GONE — every sticker now goes through the new dialog,
 * which dispatches raw TSPL2 to the TSC TTP-244 Pro via QZ Tray (USB) or
 * direct TCP (network mode). See /app/backend/routers/printing.py.
 *
 * BUILD STAMP — increment this string whenever sticker code path changes.
 * Visible in the page footer so SEPL Store users can confirm at a glance
 * they are running the latest deploy (no stale browser cache).
 */
const STOCK_IN_BUILD = "2026-04-29-W17-tspl-only-twozone";

export default function StockIn() {
  const { grnId } = useParams();
  const [grn, setGrn] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [sizeId, setSizeId] = useState("");
  const [printingRow, setPrintingRow] = useState(null);
  const [printerCfg, setPrinterCfg] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/grns/" + grnId),
      api.get("/suppliers"),
      api.get("/inventory"),
      api.get("/sticker-sizes"),
      api.get("/settings/sticker-printer"),
    ]).then(([g, s, inv, sz, pc]) => {
      setGrn(g.data); setSuppliers(s.data);
      setInventory(inv.data.filter((r) => r.grn_id === grnId));
      setSizes(sz.data); setSizeId(sz.data[0]?.id || "");
      setPrinterCfg(pc.data);
    });
  }, [grnId]);

  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "—";

  if (!grn) return <div className="p-8 text-[#0D2B5E]/60">Loading…</div>;

  const sizeObj = sizes.find((s) => s.id === sizeId);

  const printAll = async () => {
    if (!inventory.length || !sizeId || bulkBusy) return;
    if (!window.confirm(`Print ${inventory.length} sticker${inventory.length > 1 ? "s" : ""} in one batch?`)) return;
    setBulkBusy(true);
    const ids = inventory.map((r) => r.id);
    const mode = printerCfg?.connection || "usb";
    try {
      if (mode === "network") {
        const r = await api.post("/print/sticker/network-batch", { inventory_ids: ids, label_size_id: sizeId });
        toast.success(`${r.data.labels} stickers sent to ${r.data.ip}:${r.data.port}`);
      } else {
        // USB path — backend builds TSPL strings, frontend streams them via QZ Tray.
        if (!(await ensureConnected())) throw new Error("QZ Tray is not running on this workstation.");
        const printer = await findDefaultPrinter();
        if (!printer) throw new Error("No printer detected by QZ Tray.");
        const r = await api.post("/print/sticker/tspl-batch", { inventory_ids: ids, label_size_id: sizeId });
        const tsplList = (r.data.items || []).map((it) => it.tspl);
        await printRawBatch(printer, tsplList);
        toast.success(`${tsplList.length} stickers streamed via QZ Tray to ${printer}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Bulk print failed");
    }
    setBulkBusy(false);
  };

  const mode = printerCfg?.connection || "usb";
  const qzReady = isQzActive();

  return (
    <div data-testid="stock-in-page">
      <PageHeader title={`Stickers — ${grn.number}`} subtitle={`${inventory.length} boxes · ${grn.invoice_no || "—"}`}
        helpText={`Each sticker is sent as raw TSPL2 to the TSC TTP-244 Pro. ${mode === "usb" ? "USB mode requires QZ Tray running on this workstation." : `Network mode → ${printerCfg?.ip || "(IP not set)"}:${printerCfg?.port || 9100}.`}`}
        right={
          <div className="flex items-center gap-2">
            <select value={sizeId} onChange={(e) => setSizeId(e.target.value)} data-testid="sticker-size-select" className="border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
              {sizes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button onClick={() => nav(-1)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm flex items-center gap-2"><ArrowLeft size={14}/> Back</button>
            <button onClick={printAll} disabled={inventory.length === 0 || bulkBusy || (mode === "usb" && !qzReady)}
              className="bg-[#2E7D32] hover:bg-[#1b5e20] disabled:bg-[#BBB] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold"
              data-testid="print-all-btn"
              title={mode === "usb" && !qzReady ? "Start QZ Tray on this workstation to enable bulk USB print" : ""}>
              <Layers size={14}/> {bulkBusy ? "Sending…" : `Print All (${inventory.length})`}
            </button>
          </div>
        }
      />

      <div className="p-8">
        {mode === "usb" && !qzReady && (
          <div className="mb-4 bg-[#C62828]/8 border border-[#C62828]/30 px-4 py-2 rounded-sm flex items-start gap-3 text-sm" data-testid="qz-warning-banner">
            <span className="font-bold text-[#C62828]">Bulk print disabled</span>
            <span className="text-[#0D2B5E]/80">— QZ Tray is not running on this workstation. <a className="underline text-[#1565C0]" href="https://qz.io/download/" target="_blank" rel="noreferrer">Install / start QZ Tray</a> to enable Print All. Per-row Print buttons below will also use QZ Tray.</span>
          </div>
        )}
        <div className="bg-white border border-[#EEEEEE] rounded-sm p-5 mb-6 grid grid-cols-4 gap-4 text-sm">
          <KV k="GRN" v={grn.number}/>
          <KV k="Boxes" v={inventory.length}/>
          <KV k="Size" v={sizeObj ? `${sizeObj.width_mm}×${sizeObj.height_mm} mm` : "—"}/>
          <KV k="Supplier" v={supName(grn.supplier_id)}/>
          <KV k="Invoice" v={grn.invoice_no || "—"}/>
          <KV k="PO" v={grn.po_number || "—"}/>
          <KV k="Status" v={grn.status}/>
          <KV k="Total Items" v={(grn.items || []).length}/>
        </div>

        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2">Box</th>
                <th className="text-left px-4 py-2">Lot</th>
                <th className="text-left px-4 py-2">Material</th>
                <th className="text-right px-4 py-2">Qty</th>
                <th className="text-right px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {inventory.map((row, i) => (
                <tr key={row.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                  <td className="px-4 py-2 font-mono text-[#1565C0] font-bold">{row.box_no}/{row.total_boxes}</td>
                  <td className="px-4 py-2 font-mono text-xs">{row.lot_no}</td>
                  <td className="px-4 py-2 text-xs">{row.material_name} <span className="text-[#0D2B5E]/50 font-mono">· {row.material_code}</span></td>
                  <td className="px-4 py-2 font-mono text-right">{row.qty}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setPrintingRow({ ...row, supplier_name: supName(grn.supplier_id), invoice_no: grn.invoice_no })} className="text-xs bg-[#1565C0] hover:bg-[#0D2B5E] text-white px-3 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1" data-testid={`print-box-${row.box_no}`}>
                      <Printer size={11}/> Print
                    </button>
                  </td>
                </tr>
              ))}
              {inventory.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-[#0D2B5E]/50">No inventory rows for this GRN yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {printingRow && (
        <StickerPrintDialog
          inventory={printingRow}
          defaultLabelSizeId={sizeId}
          onClose={() => setPrintingRow(null)}
        />
      )}
      <div className="px-8 pb-8 -mt-2 text-[10px] text-[#0D2B5E]/40 font-mono tracking-widest uppercase" data-testid="stock-in-build-stamp">
        Build: {STOCK_IN_BUILD} · Sticker path: TSPL2 only · No browser-print fallback
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="border-l-2 border-[#1565C0] pl-3">
      <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/50 font-bold">{k}</div>
      <div className="text-sm font-medium text-[#0D2B5E] font-mono">{v}</div>
    </div>
  );
}
